import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import type { HeadcountReconciliationResult } from '@shared/types'
import { useModalFiles, isRoster, isStructured } from './useModalFiles'
import type { ModalFile } from './useModalFiles'
import ModalFileList from './ModalFileList'
import HeadcountPanel from './HeadcountPanel'
import RosterSelectionModal from './RosterSelectionModal'
import { useRosterValidation, RosterErrorList } from './useRosterValidation'
import RosterNotices from './RosterNotices'
import type { RosterAnalysis, RosterNotice } from '../utils/parseStructuredFilesForHeadcount'

export type { ModalFile }

interface Props {
  initialFiles: ModalFile[]
  initialResult?: HeadcountReconciliationResult | null
  onProceed: (finalFiles: ModalFile[]) => void
  onCancel: () => void
  onParseStructuredFiles?: (files: ModalFile[]) => Promise<HeadcountReconciliationResult | null>
  /**
   * Like `onParseStructuredFiles`, but also returns what the parser said about each
   * roster so unreadable or partly-read files are flagged before generation.
   * Takes precedence when both are given.
   */
  onAnalyzeStructuredFiles?: (files: ModalFile[]) => Promise<RosterAnalysis>
}

export default function HeadcountReconciliationModal({
  initialFiles,
  initialResult,
  onProceed,
  onCancel,
  onParseStructuredFiles,
  onAnalyzeStructuredFiles,
}: Props) {
  const { localFiles, setLocalFiles, addError, addFilesFromList, openDialog } = useModalFiles(initialFiles)
  const [localResult, setLocalResult] = useState<HeadcountReconciliationResult | null>(initialResult ?? null)
  const [rosterNotices, setRosterNotices] = useState<RosterNotice[]>([])
  const [parsingHeadcount, setParsingHeadcount] = useState(false)
  const [selectingRoster, setSelectingRoster] = useState(false)
  const { errors: rosterErrors, validating, validateThen, clearErrors } = useRosterValidation()
  useEffect(() => { clearErrors() }, [localFiles, clearErrors])
  const rosterNames = localFiles.filter((f) => isRoster(f.name)).map((f) => f.name)
  // Keep the callbacks in refs so changes to the function reference never re-trigger the effect
  const onParseRef = useRef(onParseStructuredFiles)
  useEffect(() => { onParseRef.current = onParseStructuredFiles })
  const onAnalyzeRef = useRef(onAnalyzeStructuredFiles)
  useEffect(() => { onAnalyzeRef.current = onAnalyzeStructuredFiles })

  // Captured once — used to decide whether to skip the initial-mount parse
  const initialFilesRef = useRef(localFiles)
  const initialResultRef = useRef(initialResult)

  // Re-evaluate headcount when the file set changes.
  // Skip the initial mount only when a pre-computed result was already provided by the parent;
  // when initialResult is null/undefined (InsideOut path) we must parse on mount too.
  useEffect(() => {
    if (localFiles === initialFilesRef.current && initialResultRef.current != null) return
    const analyze = onAnalyzeRef.current
    const parse = onParseRef.current
    if (!analyze && !parse) return

    // Only XLSX/CSV can be previewed; with more than one roster (JSON included) there is no single headcount yet.
    const structuredFiles = localFiles.filter((f) => isStructured(f.name))
    if (structuredFiles.length === 0 || localFiles.filter((f) => isRoster(f.name)).length > 1) {
      setLocalResult(null); setRosterNotices([]); setParsingHeadcount(false); return
    }

    let cancelled = false
    setParsingHeadcount(true)
    const analysis: Promise<RosterAnalysis> = analyze
      ? analyze(structuredFiles)
      : (parse as NonNullable<typeof parse>)(structuredFiles).then((reconciliation) => ({ reconciliation, notices: [] }))
    analysis
      .then(({ reconciliation, notices }) => {
        if (cancelled) return
        setLocalResult(reconciliation)
        setRosterNotices(notices)
      })
      .catch(() => { if (!cancelled) { setLocalResult(null); setRosterNotices([]) } })
      .finally(() => { if (!cancelled) setParsingHeadcount(false) })
    return () => { cancelled = true }
  }, [localFiles]) // intentionally only localFiles — callbacks are read via refs

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="headcount-review-title"
        className="bg-white rounded-2xl shadow-2xl w-full mx-4 flex flex-col"
        style={{ maxWidth: 540, maxHeight: '90vh' }}
      >

        {/* Header */}
        <div className="px-6 pt-5 pb-3 border-b border-gray-100">
          <h2 id="headcount-review-title" className="text-base font-bold text-gray-900">Review Files & Headcount</h2>
          <p className="text-xs text-gray-500 mt-0.5">Confirm your files and review the headcount figures before starting analysis.</p>
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          <ModalFileList
            files={localFiles}
            heading="Uploaded Files"
            emptyMessage="No files — add at least one below before proceeding."
            addError={addError}
            onRemove={(name) => setLocalFiles((prev) => prev.filter((f) => f.name !== name))}
            onDrop={(fileList) => void addFilesFromList(fileList)}
            onOpenDialog={() => void openDialog()}
          />
          <HeadcountPanel
            parsingHeadcount={parsingHeadcount}
            result={localResult}
            context="proceed"
            multipleRosters={rosterNames.length > 1}
            rosterUnreadable={rosterNotices.some((n) => n.severity === 'error')}
            hasUnpreviewableRoster={localFiles.some((f) => f.name.toLowerCase().endsWith('.json'))}
          />
          {/* The blocking list replaces the preview notices once Proceed has validated the roster. */}
          {!parsingHeadcount && rosterErrors.length === 0 && <RosterNotices notices={rosterNotices} />}
          <RosterErrorList errors={rosterErrors} />
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-100 flex flex-row gap-2">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 px-4 rounded-lg border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:border-gray-300 transition-all"
          >
            Cancel
          </button>
          <button
            disabled={localFiles.length === 0 || validating}
            onClick={() => { if (rosterNames.length > 1) setSelectingRoster(true); else void validateThen(localFiles, onProceed) }}
            className="flex-1 py-2.5 px-4 rounded-lg text-white text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
          >
            Proceed to generation
          </button>
        </div>
      </div>
      {selectingRoster && (
        <RosterSelectionModal
          rosterNames={rosterNames}
          onConfirm={(selected) => {
            setSelectingRoster(false)
            void validateThen(localFiles.filter((f) => !isRoster(f.name) || f.name === selected), onProceed)
          }}
          onCancel={() => setSelectingRoster(false)}
        />
      )}
    </div>,
    document.body
  )
}
